import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Trash2, Filter, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import RefuelEditModal from "@/components/RefuelEditModal";
import ReceiptViewer, { ReceiptEmpty } from "@/components/ReceiptViewer";
import { useAuth } from "@/context/AuthContext";
import DateRangePicker, { useDateRange } from "@/components/DateRangePicker";

export default function Refuels() {
  const { user } = useAuth();
  const isAdmin = !!(user && user.is_admin);
  const [refuels, setRefuels] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const range = useDateRange("all");

  const load = async (vid = vehicleId) => {
    setLoading(true);
    try {
      const params = { limit: 500, ...range.params };
      if (vid) params.vehicle_id = vid;
      const [r, v] = await Promise.all([api.get("/refuels", { params }), api.get("/vehicles")]);
      setRefuels(r.data);
      setVehicles(v.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vehicleId, range.preset, range.from, range.to]);

  const remove = async (r) => {
    if (!window.confirm("Delete this refuel entry?")) return;
    try {
      await api.delete(`/refuels/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    }
  };

  const vehById = Object.fromEntries(vehicles.map((v) => [v.id, v]));

  return (
    <div className="space-y-8">
      <div>
        <p className="ft-overline mb-2">HISTORY</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none">Refuel Log</h1>
        <p className="mt-3 text-[#A3A3A3]">Every fill-up with the derived KM since last, mileage, and cost-per-km.</p>
      </div>

      <div className="ft-card p-4 flex items-center gap-3 flex-wrap">
        <Filter size={16} className="text-[#FF5A1F]" />
        <span className="ft-overline">FILTER BY VEHICLE</span>
        <select
          value={vehicleId}
          onChange={(e) => setVehicleId(e.target.value)}
          className="bg-[#0A0A0A] border border-[#262626] px-3 py-2 font-mono text-white focus:outline-none focus:border-[#FF5A1F] transition-colors duration-200"
          data-testid="refuels-vehicle-filter"
        >
          <option value="">All vehicles</option>
          {vehicles.map((v) => (
            <option key={v.id} value={v.id}>{`${v.number} · ${v.name}`}</option>
          ))}
        </select>
        <span className="ml-auto text-[#737373] text-xs font-mono" data-testid="refuels-count">{refuels.length} entries</span>
      </div>

      <DateRangePicker
        preset={range.preset}
        setPreset={range.setPreset}
        from={range.from}
        setFrom={range.setFrom}
        to={range.to}
        setTo={range.setTo}
        onRefresh={() => load()}
        testidPrefix="refuels-"
      />

      <div className="ft-card p-0 overflow-auto">
        {loading ? (
          <div className="p-12 text-center text-[#737373]">Loading…</div>
        ) : refuels.length === 0 ? (
          <div className="p-12 text-center text-[#A3A3A3]">No refuels found.</div>
        ) : (
          <table className="w-full" data-testid="refuels-table">
            <thead>
              <tr>
                {["Date", "Vehicle", "Odometer", "Litres", "Rate", "Amount", "Km Since", "Mileage", "₹/km", "Station", "Receipt", ""].map((h) => (
                  <th key={h} className="text-left py-4 px-3 text-xs uppercase tracking-wider font-bold text-[#737373] border-b border-[#262626]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="font-mono text-sm">
              {refuels.map((r) => {
                const v = vehById[r.vehicle_id];
                return (
                  <tr key={r.id} className="border-b border-[#262626] hover:bg-[#1A1A1A] transition-colors duration-150" data-testid={`refuel-row-${r.id}`}>
                    <td className="py-3 px-3 text-[#A3A3A3]">{(r.refuelled_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="py-3 px-3">
                      {v ? (
                        <Link to={`/vehicles/${v.id}`} className="text-[#FF5A1F] hover:underline" data-testid={`refuel-vehicle-link-${r.id}`}>
                          {v.number}
                        </Link>
                      ) : "—"}
                    </td>
                    <td className="py-3 px-3 text-right">{r.odometer?.toLocaleString?.()}</td>
                    <td className="py-3 px-3 text-right">{r.litres?.toFixed(2)}</td>
                    <td className="py-3 px-3 text-right">₹{r.rate?.toFixed(2)}</td>
                    <td className="py-3 px-3 text-right text-white font-bold">₹{r.amount?.toFixed(0)}</td>
                    <td className="py-3 px-3 text-right">{r.km_since_last ?? <span className="text-[#737373]">—</span>}</td>
                    <td className="py-3 px-3 text-right text-[#FF5A1F]">{r.mileage ? `${r.mileage} km/l` : <span className="text-[#737373]">—</span>}</td>
                    <td className="py-3 px-3 text-right">{r.cost_per_km ? `₹${r.cost_per_km}` : <span className="text-[#737373]">—</span>}</td>
                    <td className="py-3 px-3 text-[#A3A3A3]">{r.station || "—"}</td>
                    <td className="py-3 px-3">
                      {r.receipt_path ? <ReceiptViewer path={r.receipt_path} /> : <ReceiptEmpty />}
                    </td>
                    <td className="py-3 px-3 text-right">
                      <div className="inline-flex gap-1">
                        {isAdmin && (
                          <button onClick={() => setEditing(r)} className="text-[#3B82F6] hover:text-white hover:bg-[#3B82F6] p-2 transition-colors duration-200" data-testid={`refuel-edit-${r.id}`}>
                            <Pencil size={14} />
                          </button>
                        )}
                        <button onClick={() => remove(r)} className="text-[#EF4444] hover:text-white hover:bg-[#EF4444] p-2 transition-colors duration-200" data-testid={`refuel-delete-${r.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <RefuelEditModal
        open={!!editing}
        refuel={editing}
        vehicles={vehicles}
        onClose={() => setEditing(null)}
        onSaved={() => load()}
      />
    </div>
  );
}
